import type {
  AdminCommerceAdjustCreditsReply,
  AdminCommerceAdjustCreditsRequest,
  AdminCommerceCostChainsReply,
  AdminCommerceCostDimensionsReply,
  AdminCommerceCostRequest,
  AdminCommerceCostSeriesReply,
  AdminCommerceCostSummaryReply,
  AdminCommerceCreateRefundRequest,
  AdminCommerceCreditHistoryListReply,
  AdminCommerceCreditHistoryListRequest,
  AdminCommerceCreditOverviewReply,
  AdminCommerceMembershipDetail,
  AdminCommerceOrderDetail,
  AdminCommerceOrderListReply,
  AdminCommerceOrderListRequest,
  AdminCommerceRefundPreview,
  AdminCommerceRefundPreviewRequest,
  AdminCommerceRefundStatus,
} from '@/types';
import api from './api';

const costRequestConfig = (params: AdminCommerceCostRequest) => ({
  params,
  paramsSerializer: { indexes: null as null },
});

export const adminCommerceApi = {
  listOrders: async (
    params: AdminCommerceOrderListRequest,
  ): Promise<AdminCommerceOrderListReply> => {
    return api.get('/api/v1/admin-commerce/orders', { params });
  },

  getOrder: async (
    brandId: string,
    orderNo: string,
  ): Promise<AdminCommerceOrderDetail> => {
    return api.get(
      `/api/v1/admin-commerce/orders/${encodeURIComponent(orderNo)}`,
      { params: { brandId } },
    );
  },

  getMembership: async (
    brandId: string,
    userId: string,
  ): Promise<AdminCommerceMembershipDetail> => {
    return api.get(
      `/api/v1/admin-commerce/users/${encodeURIComponent(userId)}/membership`,
      { params: { brandId } },
    );
  },

  previewRefund: async (
    brandId: string,
    orderNo: string,
    data: AdminCommerceRefundPreviewRequest,
  ): Promise<AdminCommerceRefundPreview> => {
    return api.post(
      `/api/v1/admin-commerce/orders/${encodeURIComponent(orderNo)}/refund-preview`,
      data,
      { params: { brandId } },
    );
  },

  createRefund: async (
    brandId: string,
    orderNo: string,
    data: AdminCommerceCreateRefundRequest,
  ): Promise<AdminCommerceRefundStatus> => {
    return api.post(
      `/api/v1/admin-commerce/orders/${encodeURIComponent(orderNo)}/refunds`,
      data,
      { params: { brandId } },
    );
  },

  getRefund: async (
    brandId: string,
    orderNo: string,
    refundRequestNo: string,
  ): Promise<AdminCommerceRefundStatus> => {
    return api.get(
      `/api/v1/admin-commerce/orders/${encodeURIComponent(orderNo)}/refunds/${encodeURIComponent(refundRequestNo)}`,
      { params: { brandId } },
    );
  },

  getCreditsOverview: async (params: {
    userId: string;
    appId: string;
  }): Promise<AdminCommerceCreditOverviewReply> => {
    return api.get('/api/v1/admin-commerce/credits/overview', { params });
  },

  listCreditHistories: async (
    params: AdminCommerceCreditHistoryListRequest,
  ): Promise<AdminCommerceCreditHistoryListReply> => {
    return api.get('/api/v1/admin-commerce/credits/histories', { params });
  },

  adjustCredits: async (
    data: AdminCommerceAdjustCreditsRequest,
  ): Promise<AdminCommerceAdjustCreditsReply> => {
    return api.post('/api/v1/admin-commerce/credits/adjust', data);
  },

  getCostSummary: async (
    params: AdminCommerceCostRequest,
  ): Promise<AdminCommerceCostSummaryReply> => {
    return api.get(
      '/api/v1/admin-commerce/costs/summary',
      costRequestConfig(params),
    );
  },

  listCostSeries: async (
    params: AdminCommerceCostRequest,
  ): Promise<AdminCommerceCostSeriesReply> => {
    return api.get(
      '/api/v1/admin-commerce/costs/series',
      costRequestConfig(params),
    );
  },

  getCostDimensions: async (
    params: AdminCommerceCostRequest,
  ): Promise<AdminCommerceCostDimensionsReply> => {
    return api.get(
      '/api/v1/admin-commerce/costs/dimensions',
      costRequestConfig(params),
    );
  },

  listCostChains: async (
    params: AdminCommerceCostRequest,
  ): Promise<AdminCommerceCostChainsReply> => {
    return api.get(
      '/api/v1/admin-commerce/costs/chains',
      costRequestConfig(params),
    );
  },
};
